import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useChemistryStore } from '../../stores/chemistryStore';
import { useNavigationStore } from '../../stores/navigationStore';
import { useTaskStore } from '../../stores/taskStore';
import MoleculeGrid, { ColumnStats, HeatmapSettings, getMoleculeName } from '../../components/chemistry/MoleculeGrid';
import type { GridApi } from 'ag-grid-enterprise';
import ReactionGrid from '../../components/chemistry/ReactionGrid';
import BiologicsGrid from '../../components/chemistry/BiologicsGrid';
import SearchPanel from '../../components/chemistry/SearchPanel';
import TableManagementDropdown from '../../components/chemistry/TableManagementDropdown';
import ProjectTargetUniverseSection from '../../components/chemistry/ProjectTargetUniverseSection';
import HeatmapPanel from '../../components/chemistry/HeatmapPanel';
import PatentCollisionMap from '../../components/chemistry/PatentCollisionMap';
import { usePatentMapStore } from '../../stores/patentMapStore';
import MoleculeColumnHeader from '../../components/chemistry/MoleculeColumnHeader';
import PageHeader from '../../components/common/PageHeader';
import ExportButton from '../../components/export/ExportButton';
import { GuestGate } from '../../components/common/UpgradePrompt';
import UpgradePrompt from '../../components/common/UpgradePrompt';
import ShareDialog from '../../components/sharing/ShareDialog';
import ShareSecurityBriefing from '../../components/sharing/ShareSecurityBriefing';
import ShareActivityPanel from '../../components/sharing/ShareActivityPanel';
import SecureShareBanner from '../../components/sharing/SecureShareBanner';
import DuplicateDialog from '../../components/sharing/DuplicateDialog';
import DedupDialog from '../../components/chemistry/DedupDialog';
import { useAuth } from '../../context/AuthContext';
import { chemistryApi } from '../../api/chemistry';
import { Molecule, Project } from '../../types/chemistry';
import CompoundDetailPanel, { DetailTab } from '../../components/chemistry/CompoundDetailPanel';
import RunIntelligenceButton from '../../components/chemistry/RunIntelligenceButton';
import ViewModeSwitcher, { ViewMode } from '../../components/chemistry/ViewModeSwitcher';
import OverflowMenu, { OverflowItem } from '../../components/ui/OverflowMenu';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import AnalyseView from '../../components/chemistry/AnalyseView';
import DynamicWatermark from '../../components/security/DynamicWatermark';
import CompoundComparison from '../../components/chemistry/CompoundComparison';
import CompareAllTable from '../../components/chemistry/CompareAllTable';
import BatchRegistrationModal from '../../components/chemistry/BatchRegistrationModal';
import SelectionActionBar from '../../components/chemistry/SelectionActionBar';
import SubmitForAssayModal from '../../components/assays/SubmitForAssayModal';
import FilePanel from '../../components/chemistry/FilePanel';
import SARScatterChart from '../../components/chemistry/SARScatterChart';
import { useUIStore } from '../../stores/uiStore';
import { requestedMoleculeId, WorkbenchFocus } from '../../utils/workbenchNavigation';
import { getStoredToken } from '../../utils/tokenStorage';
import { registrationAPI } from '../../services/registrationApi';

const EditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromProjectId = (location.state as any)?.fromProjectId as number | undefined;
  const autoEnrich = (location.state as any)?.autoEnrich as boolean | undefined;
  const { isGuest, user } = useAuth();
  const isMember = !isGuest;
  const bp = useBreakpoint();

  // Detect "All Compounds" mode — special ID format: project-{projectId}
  const allCompoundsMatch = id?.match(/^project-(\d+)$/);
  const isAllCompoundsMode = !!allCompoundsMatch;
  const allCompoundsProjectId = allCompoundsMatch ? parseInt(allCompoundsMatch[1], 10) : null;
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showSecurityBriefing, setShowSecurityBriefing] = useState(false);
  const [showShareActivity, setShowShareActivity] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showDedupDialog, setShowDedupDialog] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [refLibraryStatus, setRefLibraryStatus] = useState<'none' | 'pending' | 'approved' | 'rejected'>('none');
  const isAdmin = user?.role === 'admin' || user?.user_type === 'admin' || user?.user_type === 'super_admin';
  const showNotification = useUIStore((s) => s.showNotification);
  const {
    currentFile,
    molecules,
    totalMolecules,
    isLoadingMolecules,
    moleculesError,
    searchResults,
    isSearching,
    fetchFileInfo,
    fetchMolecules,
    clearSearch,
    searchStructure,
  } = useChemistryStore();

  // Track if we've run the initial search from navigation state
  const initialSearchRanRef = useRef(false);
  // Import progress polling
  const [importProgress, setImportProgress] = useState<{
    processed: number;
    total: number;
    status: string;
  } | null>(null);
  const importPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Threshold for switching to SSRM (server-side row model)
  // Default to SSRM until we KNOW the file is small (file loaded AND ≤ threshold)
  const SSRM_THRESHOLD = 1000;
  const fileInfoLoaded = !!currentFile && totalMolecules > 0;
  const useSSRM = !fileInfoLoaded || totalMolecules > SSRM_THRESHOLD;

  console.log('[DEBUG EditorPage] totalMolecules:', totalMolecules, 'fileInfoLoaded:', fileInfoLoaded, 'useSSRM:', useSSRM);

  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedMolecules, setSelectedMolecules] = useState<Molecule[]>([]);
  const [attributeFilteredMolecules, setAttributeFilteredMolecules] = useState<Molecule[] | null>(null);
  const [highlightedIds, setHighlightedIds] = useState<Set<number> | null>(null);
  const [showMatchesOnly, setShowMatchesOnly] = useState(true);
  const [isTransposed, setIsTransposed] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [heatmapSettings, setHeatmapSettings] = useState<HeatmapSettings>({
    intensity: 0.4,
    columns: {},
  });
  const [columnStats, setColumnStats] = useState<ColumnStats>({});
  const [showHeatmapPanel, setShowHeatmapPanel] = useState(false);
  const heatmapChannelRef = useRef<BroadcastChannel | null>(null);
  const [showCollisionMap, setShowCollisionMap] = useState(false);
  const [gridRefreshTrigger, setGridRefreshTrigger] = useState(0);
  const gridApiRef = useRef<GridApi | null>(null);
  const [resetColumnsTrigger, setResetColumnsTrigger] = useState(0);
  const [selectAllTrigger, setSelectAllTrigger] = useState(0);
  const [deselectAllTrigger, setDeselectAllTrigger] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [isLocked, setIsLocked] = useState(true);  // Grid editing locked by default
  const [showComparison, setShowComparison] = useState(false);
  const [showCompareAll, setShowCompareAll] = useState(false);
  const [showBatchRegistration, setShowBatchRegistration] = useState(false);
  const [showAssayModal, setShowAssayModal] = useState(false);
  const [detailPanelMolecule, setDetailPanelMolecule] = useState<Molecule | null>(null);
  const [workbenchFocus, setWorkbenchFocus] = useState<WorkbenchFocus | null>(null);
  const incomingMoleculeId = requestedMoleculeId(location.search, location.state);
  const [detailPanelTab, setDetailPanelTab] = useState<DetailTab | null>(null);
  const [isDetailPinned, setIsDetailPinned] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedMoleculeIds, setSelectedMoleculeIds] = useState<Set<number>>(new Set());

  // Sync selectedMoleculeIds when grid selection (selectedMolecules) changes
  useEffect(() => {
    setSelectedMoleculeIds(new Set(selectedMolecules.map(m => m.id)));
  }, [selectedMolecules]);

  const [enrichTrigger, setEnrichTrigger] = useState(0);
  const autoEnrichFired = useRef(false);

  // Auto-trigger enrichment when navigating from import — only after molecules are loaded
  useEffect(() => {
    if (autoEnrich && !autoEnrichFired.current && totalMolecules > 0) {
      autoEnrichFired.current = true;
      // Small delay so RunIntelligenceButton is mounted and ready
      const t = setTimeout(() => setEnrichTrigger(n => n + 1), 500);
      return () => clearTimeout(t);
    }
  }, [autoEnrich, totalMolecules]);

  const [exportTrigger, setExportTrigger] = useState(0);
  const [columnVisibilityTrigger, setColumnVisibilityTrigger] = useState(0);
  const [columnVisibilityAction, setColumnVisibilityAction] = useState<{ columnId: string; visible: boolean } | null>(null);

  // BroadcastChannel for heatmap popout sync
  useEffect(() => {
    const ch = new BroadcastChannel('heatmap-settings');
    heatmapChannelRef.current = ch;

    ch.onmessage = (e) => {
      if (e.data?.type === 'update') {
        setHeatmapSettings(e.data.heatmapSettings);
      } else if (e.data?.type === 'popout-ready') {
        // Popout window is ready — send current state
        ch.postMessage({ type: 'init', heatmapSettings, columnStats });
      }
    };

    return () => ch.close();
  }, [heatmapSettings, columnStats]);

  // Numeric property columns for AnalyseView
  const numericPropertyColumns = useMemo(() => {
    const moleculeList = searchResults ? searchResults.molecules : molecules;
    if (!moleculeList || moleculeList.length === 0) return [];
    const propertyKeys = new Set<string>();
    moleculeList.forEach(molecule => Object.keys(molecule.properties || {}).forEach(key => propertyKeys.add(key)));
    const numericPattern = /^(?:<=|>=|<|>|~|≈)?\s*[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/;
    return Array.from(propertyKeys).filter(key => {
      if (['id', 'file_id', 'smiles', 'molfile', 'inchi', 'structure', 'svg', 'image'].includes(key)) return false;
      return moleculeList.some(molecule => {
        const val = molecule.properties?.[key];
        return typeof val === 'number' || (typeof val === 'string' && numericPattern.test(val.trim()));
      });
    });
  }, [molecules, searchResults]);

  // Column units map for grid headers and chart axes
  const columnUnitsMap = useMemo(() => {
    const map: Record<string, { unit: string | null; unitType: string | null; displayName: string | null }> = {};
    if (currentFile?.columns) {
      for (const col of currentFile.columns) {
        if (col.unit || col.displayName) {
          map[col.name] = {
            unit: col.unit || null,
            unitType: col.unitType || null,
            displayName: col.displayName || null,
          };
        }
      }
    }
    return map;
  }, [currentFile]);

  // Unit overrides from MoleculeGrid (for syncing with AnalyseView axis labels)
  const [unitOverrides, setUnitOverrides] = useState<Record<string, { originalUnit: string; displayUnit: string; unitType: string }>>({});

  // Edit history for undo functionality
  interface EditHistoryEntry {
    moleculeId: number;
    propertyKey: string;
    oldValue: any;
    newValue: any;
  }
  const [editHistory, setEditHistory] = useState<EditHistoryEntry[]>([]);

  // File rename modal state
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  // Reaction count for the header (reactions live in their own table, not molecules)
  const [reactionCount, setReactionCount] = useState<number | null>(null);

  // Detect if file is a reactions file (by fileType or extension)
  const isReactionsFile = useMemo(() => {
    if (!currentFile) return false;
    // Check fileType first (from backend)
    if ((currentFile as any).fileType === 'reactions') return true;
    // Fallback to file extension check
    const fileName = currentFile.originalFileName || currentFile.fileName || '';
    const ext = fileName.toLowerCase().split('.').pop();
    return ext === 'rxn' || ext === 'rdf';
  }, [currentFile]);

  // Detect if file is a biologics file (by fileType or extension)
  const isBiologicsFile = useMemo(() => {
    if (!currentFile) return false;
    const fileName = currentFile.originalFileName || currentFile.fileName || '';
    const ext = fileName.toLowerCase().split('.').pop();
    // CIF files imported via chemistry pipeline are molecules, not biologics
    if (ext === 'cif') return false;
    // Check fileType first (from backend)
    if ((currentFile as any).fileType === 'biologics') return true;
    // Fallback to file extension check
    return ['fasta', 'fa', 'faa', 'fna', 'ffn', 'pdb'].includes(ext || '');
  }, [currentFile]);

  // Redirect biologics files to the dedicated Biologics viewer
  useEffect(() => {
    if (isBiologicsFile && id) {
      navigate(`/omics?file=${id}`, { replace: true });
    }
  }, [isBiologicsFile, id, navigate]);

  // Reaction files: header count comes from the reactions table (the molecules
  // counter is 0 for these files), matching the grid's "N reactions in file".
  useEffect(() => {
    if (isReactionsFile && id) {
      chemistryApi.getReactionCount(parseInt(id, 10))
        .then((res) => setReactionCount(res.count))
        .catch(() => setReactionCount(null));
    }
  }, [isReactionsFile, id]);

  // Reset search state when file changes
  useEffect(() => {
    // Reset the initial search flag when navigating to a different file
    initialSearchRanRef.current = false;
    setDetailPanelMolecule(null);
    clearSearch();
    setAttributeFilteredMolecules(null);
    setHighlightedIds(null);
    setShowMatchesOnly(true);
  }, [id, clearSearch]);

  // Share context from navigation state (when coming from SharedFileLanding)
  const shareState = location.state as { shareToken?: string; sharedByName?: string; permission?: string } | null;

  // Run search from navigation state (when coming from FilesPage search results)
  // This only runs once per navigation, avoiding race conditions
  useEffect(() => {
    const state = location.state as { searchQuery?: string; searchType?: 'substructure' | 'similarity' | 'exact' } | null;
    if (id && state?.searchQuery && state?.searchType && !initialSearchRanRef.current) {
      initialSearchRanRef.current = true;
      searchStructure(id, state.searchQuery, state.searchType);
    }
  }, [id, location.state, searchStructure]);

  // Fetch project info for breadcrumb (skip in all-compounds mode — handled separately)
  useEffect(() => {
    if (currentFile?.projectId && !isAllCompoundsMode) {
      chemistryApi.getProject(currentFile.projectId).then(setCurrentProject).catch(() => {});
    }
  }, [currentFile?.projectId, isAllCompoundsMode]);

  // Sync project/file context to navigation store for breadcrumbs
  useEffect(() => {
    if (currentProject) {
      useNavigationStore.getState().setProject(currentProject.id, currentProject.name);
    }
    if (currentFile) {
      useNavigationStore.getState().setFile(
        currentFile.id ? Number(currentFile.id) : null,
        currentFile.fileName || null,
      );
    }
  }, [currentProject, currentFile]);

  // Fetch reference library status for this file (skip non-numeric IDs like "project-4")
  useEffect(() => {
    if (!id || isNaN(Number(id))) return;
    const token = getStoredToken();
    if (!token) return;
    fetch(`/api/v1/reference-library/status/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setRefLibraryStatus(data.status); })
      .catch(() => {});
  }, [id]);

  // Auto-open Compare All when ?view=compareAll is in the URL (popout window)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('view') === 'compareAll') {
      setShowCompareAll(true);
    }
  }, [location.search]);

  // Resolve the exact molecule on each navigation, including refresh/back/new tab.
  // Verify source-file membership before opening details or selecting any row.
  useEffect(() => {
    setWorkbenchFocus(null);
    if (!incomingMoleculeId || !id || !/^\d+$/.test(id)) return;
    let cancelled = false;
    setDetailPanelMolecule(null);
    setViewMode('grid');
    setIsTransposed(false);
    clearSearch();
    setAttributeFilteredMolecules(null);
    setHighlightedIds(null);

    chemistryApi.getMolecule(incomingMoleculeId, true).then((data) => {
      if (cancelled) return;
      if (data.file_id !== Number(id)) throw new Error('Linked compound is not in this source file');
      setDetailPanelMolecule({
        ...data,
        id: data.id,
        mol_index: data.file_row_index ?? 0,
        molfile: '',
        smiles: data.smiles || '',
        properties: data.properties || {},
      });
      setDetailPanelTab(new URLSearchParams(location.search).get('view') === 'complex' ? 'complex' : null);
      setWorkbenchFocus({
        moleculeId: data.id,
        rowIndex: data.file_row_index ?? null,
        requestKey: `${location.key}:${id}:${data.id}`,
      });
    }).catch(() => {
      if (!cancelled) showNotification('error', 'The linked compound is unavailable in this file. No substitute was selected.');
    });
    return () => { cancelled = true; };
  }, [incomingMoleculeId, id, location.key, location.search, clearSearch, showNotification]);

  // DEBUG: Track currentFile changes
  useEffect(() => {
    console.log('[EditorPage] currentFile CHANGED:', {
      originalFileName: currentFile?.originalFileName,
      fileName: currentFile?.fileName,
      id: currentFile?.id
    });
  }, [currentFile]);

  // Handle search panel filter results
  const handleFilteredResults = useCallback((filtered: Molecule[] | null, matchIds: Set<number> | null) => {
    setAttributeFilteredMolecules(filtered);
    setHighlightedIds(matchIds);
  }, []);

  // Handle show matches only toggle from SearchPanel
  const handleShowMatchesOnlyChange = useCallback((checked: boolean) => {
    setShowMatchesOnly(checked);
  }, []);

  // Run the substructure search in place against the current file — no navigation.
  const handleSearchParticipant = useCallback((smiles: string) => {
    if (!id) return;
    console.log('[EditorPage] Searching for participant (in place):', smiles);
    searchStructure(id, smiles, 'substructure');
  }, [id, searchStructure]);

  // Get column names from file metadata (preferred) or extract from molecules
  const columns = useMemo(() => {
    // Merge columns from file metadata AND actual molecule properties
    // so dynamically-added columns (from data) always appear in dropdowns
    const allColumns = new Set<string>();

    // Source 1: file metadata columns (non-hidden, non-deleted)
    if (currentFile?.columns && currentFile.columns.length > 0) {
      currentFile.columns
        .filter(col => !col.hidden && !col.deleted)
        .forEach(col => allColumns.add(col.name));
    }

    // Source 2: actual molecule property keys (covers columns not yet in metadata)
    const moleculeList = searchResults ? searchResults.molecules : molecules;
    moleculeList.forEach((mol) => {
      if (mol.properties) {
        Object.keys(mol.properties).forEach((key) => allColumns.add(key));
      }
    });

    return Array.from(allColumns).sort();
  }, [currentFile, molecules, searchResults]);

  // Live column reader — queries AG Grid API for current property columns
  const getGridColumns = useCallback((): string[] => {
    const api = gridApiRef.current;
    if (!api) {
      console.log('[getGridColumns] No gridApi ref — using fallback columns:', columns.length);
      return columns;
    }

    const seen = new Set<string>();
    const PROTECTED = new Set(['structure', 'smiles']);

    // Flatten column groups recursively — enrichment columns are nested in groups
    const flattenColDefs = (defs: any[]): any[] => {
      const result: any[] = [];
      for (const def of defs) {
        if (def.children) {
          result.push(...flattenColDefs(def.children));
        } else if (def.field) {
          result.push(def);
        }
      }
      return result;
    };

    try {
      const defs = api.getColumnDefs?.() || [];
      const flat = flattenColDefs(defs);
      for (const def of flat) {
        const field = (def as any).field || '';
        if (!field || field.startsWith('_') || PROTECTED.has(field)) continue;
        if (field.startsWith('properties.')) {
          seen.add(field.replace('properties.', ''));
        } else {
          seen.add(field);
        }
      }
    } catch (e) {
      console.warn('[getGridColumns] getColumnDefs() failed:', e);
    }

    const result = seen.size > 0 ? Array.from(seen).sort() : columns;
    console.log('[getGridColumns] Found', seen.size, 'columns from grid API:', Array.from(seen));
    console.log('[getGridColumns] returning:', result);
    return result;
  }, [columns]);

  // Max molecules shown in transpose view (structure headers are expensive)
  const TRANSPOSE_MOL_LIMIT = 50;

  // Transpose function - swap rows and columns
  const transposeMolecules = useCallback((mols: Molecule[], cols: string[]) => {
    if (!mols.length) return { data: [], columns: [], truncated: false };

    const truncated = mols.length > TRANSPOSE_MOL_LIMIT;
    const visibleMols = truncated ? mols.slice(0, TRANSPOSE_MOL_LIMIT) : mols;

    // Create transposed rows - one row per property
    const transposedData = cols.map(colName => {
      const row: Record<string, any> = { property: colName };
      visibleMols.forEach((mol, idx) => {
        row[`mol_${idx + 1}`] = mol.properties?.[colName] ?? '';
      });
      return row;
    });

    // Create transposed columns with molecule structure headers
    const transposedColumns = [
      { field: 'property', headerName: 'Property', pinned: 'left' as const, width: 150 },
      ...visibleMols.map((mol, idx) => ({
        field: `mol_${idx + 1}`,
        headerName: getMoleculeName(mol.properties) || `Mol ${mol.id || idx + 1}`,
        width: 80,
        headerComponent: MoleculeColumnHeader,
        headerComponentParams: {
          smiles: mol.smiles,
          name: getMoleculeName(mol.properties),
          moleculeId: mol.id || idx + 1,
        },
      })),
    ];

    return { data: transposedData, columns: transposedColumns, truncated };
  }, []);

  // ─── All Compounds mode: fetch project info and set up virtual file ───
  const [allCompoundsProject, setAllCompoundsProject] = useState<Project | null>(null);
  const [allCompoundsTotalCount, setAllCompoundsTotalCount] = useState(0);

  useEffect(() => {
    if (!isAllCompoundsMode || !allCompoundsProjectId) return;
    // Fetch project info and total count
    chemistryApi.getProject(allCompoundsProjectId).then(project => {
      setAllCompoundsProject(project);
      setCurrentProject(project);
      // Create a virtual currentFile for the toolbar
      const virtualFile = {
        id: id || '',
        fileName: `All Compounds — ${project.name}`,
        originalFileName: `All Compounds — ${project.name}`,
        moleculesLibraryCount: 0,
        moleculesInvalid: 0,
        propertiesCount: 0,
        status: 'completed',
        projectId: allCompoundsProjectId,
      } as any;
      useChemistryStore.getState().setCurrentFile(virtualFile);
    }).catch(() => {});
    // Fetch total compound count from the SSRM endpoint
    chemistryApi.queryProjectCompounds(allCompoundsProjectId, { startRow: 0, endRow: 1 })
      .then(res => {
        setAllCompoundsTotalCount(res.lastRow);
        // Update totalMolecules in store for header display
        useChemistryStore.setState({ totalMolecules: res.lastRow, isLoadingMolecules: false });
      })
      .catch(() => {});
  }, [isAllCompoundsMode, allCompoundsProjectId, id]);

  // All Compounds SSRM query function
  const allCompoundsQueryFn = useCallback(async (params: {
    startRow: number;
    endRow: number;
    sortModel?: Array<{ colId: string; sort: string }>;
    filterModel?: Record<string, any>;
  }) => {
    if (!allCompoundsProjectId) return { rows: [], lastRow: 0 };
    return chemistryApi.queryProjectCompounds(allCompoundsProjectId, params);
  }, [allCompoundsProjectId]);

  // Step 1: Fetch file info (fast - just metadata with molecule count)
  // Skip for All Compounds mode (handled above)
  useEffect(() => {
    if (id && !isAllCompoundsMode) {
      fetchFileInfo(id);
    }
  }, [id, isAllCompoundsMode, fetchFileInfo]);

  // Step 1b: Poll for import progress when file is processing/pending
  useEffect(() => {
    if (!id || isAllCompoundsMode) return;
    const status = currentFile?.status;
    const isProcessing = status === 'processing' || status === 'pending';

    if (isProcessing) {
      // Set initial progress from currentFile
      setImportProgress({
        processed: currentFile?.moleculesProcessed || 0,
        total: currentFile?.moleculesTotal || 0,
        status: status,
      });

      // Poll while the server reports active work, but never forever. A stale
      // database status must become an actionable error rather than a permanent
      // one-second request loop.
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts += 1;
        if (attempts >= 120) {
          clearInterval(poll);
          importPollRef.current = null;
          setImportProgress(prev => prev ? { ...prev, status: 'failed' } : null);
          showNotification('error', 'Import status did not complete. Refresh or ask an administrator to reconcile this file.');
          return;
        }
        try {
          const file = await chemistryApi.getFile(id);
          const done = file.status === 'completed' || file.status === 'completed_with_errors';
          const failed = file.status === 'error' || file.status === 'failed' || file.status === 'cancelled';

          setImportProgress({
            processed: file.moleculesProcessed || 0,
            total: file.moleculesTotal || 0,
            status: file.status,
          });

          if (done || failed) {
            clearInterval(poll);
            importPollRef.current = null;
            // Refresh file info to load the completed file
            if (done) {
              setTimeout(() => {
                fetchFileInfo(id);
                setImportProgress(null);
              }, 1500); // Brief pause to show "complete" state
            }
          }
        } catch {
          // Silently continue polling on error
        }
      }, 1000);

      importPollRef.current = poll;
      return () => { clearInterval(poll); importPollRef.current = null; };
    } else {
      // File is not processing — clear any stale progress
      if (importProgress && importProgress.status !== currentFile?.status) {
        setImportProgress(null);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isAllCompoundsMode, currentFile?.status]);

  // Step 2: For small files, also fetch all molecules for client-side mode
  // Skip for All Compounds mode (always uses SSRM)
  useEffect(() => {
    if (id && !isAllCompoundsMode && currentFile && totalMolecules > 0 && totalMolecules <= SSRM_THRESHOLD && molecules.length === 0) {
      console.log('[EditorPage] Small file detected, loading all molecules for client-side mode');
      fetchMolecules(id, 1, totalMolecules);
    }
  }, [id, isAllCompoundsMode, currentFile, totalMolecules, molecules.length, fetchMolecules]);

  // Step 3: Load all molecules when transpose, analyse, or SAR view is enabled, or Compare All is open
  // Check if we need to load more molecules (not just if empty, but if we don't have ALL)
  useEffect(() => {
    if ((isTransposed || viewMode === 'analyse' || viewMode === 'sar' || showCompareAll) && id && totalMolecules > 0 && molecules.length < totalMolecules) {
      console.log('[EditorPage] Transpose/Analyse/SAR/CompareAll enabled, loading all', totalMolecules, 'molecules (have', molecules.length, ')');
      fetchMolecules(id, 1, totalMolecules);
    }
  }, [isTransposed, viewMode, showCompareAll, id, totalMolecules, molecules.length, fetchMolecules]);

  // State to track if we're loading for transpose
  const [isLoadingForTranspose, setIsLoadingForTranspose] = useState(false);

  // Handler for toggling transpose - loads data first if needed
  // Uses currentFile from component scope (already has moleculesLibraryCount from fetchFileInfo)
  const handleToggleTranspose = useCallback(async () => {
    // Use currentFile from component scope - it has moleculesLibraryCount from fetchFileInfo
    const total = currentFile?.moleculesLibraryCount || 0;
    const currentMoleculeCount = molecules.length;

    console.log('[EditorPage] handleToggleTranspose:', {
      isTransposed,
      total,
      currentMoleculeCount,
      currentFileName: currentFile?.fileName
    });

    if (!isTransposed && id) {
      // Enabling transpose - need to ensure we have all molecules first
      if (total > 0 && currentMoleculeCount < total) {
        console.log('[EditorPage] Loading all', total, 'molecules before enabling transpose...');
        setIsLoadingForTranspose(true);
        try {
          // Fetch all molecules - the API getMolecules applies pagination,
          // so pass total as limit to get all
          await fetchMolecules(id, 1, total);
          const store = useChemistryStore.getState();
          console.log('[EditorPage] After load, store has:', store.molecules.length, 'molecules');
        } catch (error) {
          console.error('[EditorPage] Failed to load molecules for transpose:', error);
        } finally {
          setIsLoadingForTranspose(false);
        }
      }
    }
    // Column state save/restore is handled by MoleculeGrid's isTransposed useEffect
    setIsTransposed(prev => !prev);
  }, [isTransposed, id, currentFile, molecules.length, fetchMolecules]);

  const { addTask, updateTask } = useTaskStore();

  // Start polling for download task
  const startDownloadPolling = useCallback((taskId: string, fileName: string) => {
    const poll = async () => {
      try {
        const status = await chemistryApi.getDownloadStatus(taskId);

        if (status.status === 'COMPLETED') {
          updateTask(taskId, {
            status: 'completed',
            percent: 100,
            downloadUrl: status.downloadUrl,
          });
          return; // Stop polling
        } else if (status.status === 'ERROR') {
          updateTask(taskId, { status: 'error', error: 'Download failed' });
          return; // Stop polling
        } else {
          // Update progress
          updateTask(taskId, {
            percent: status.percent || 0,
            totalMolecules: status.totalMolecules || 0,
          });
          setTimeout(poll, 1000);
        }
      } catch (err: any) {
        if (err.response?.status === 404) {
          setTimeout(poll, 1000);
        } else {
          updateTask(taskId, { status: 'error', error: err.message || 'Failed to check status' });
        }
      }
    };

    setTimeout(poll, 500);
  }, [updateTask]);

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    // Toggle: if clicking the same mode, revert to grid
    setViewMode(prev => {
      const next = (prev === mode && mode !== 'grid') ? 'grid' : mode;
      setIsTransposed(next === 'transpose');
      setShowHeatmap(next === 'heatmap');
      setShowCollisionMap(next === 'patentMap');
      setShowHeatmapPanel(next === 'heatmap');
      return next;
    });
  }, []);

  // Scroll to molecule in grid when switching back from patent map
  const pendingScrollId = usePatentMapStore(s => s.pendingScrollMoleculeId);
  const clearPendingScroll = usePatentMapStore(s => s.clearPendingScroll);

  useEffect(() => {
    if (viewMode === 'grid' && pendingScrollId != null && gridApiRef.current) {
      // Small delay to let grid render
      const timer = setTimeout(() => {
        const api = gridApiRef.current;
        if (!api) return;
        api.forEachNode((node: any) => {
          if (node.data?.id === pendingScrollId) {
            api.ensureNodeVisible(node, 'middle');
            node.setSelected(true);
          }
        });
        clearPendingScroll();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [viewMode, pendingScrollId, clearPendingScroll]);

  const handleRegister = useCallback(() => {
    if (viewMode !== 'grid') {
      handleViewModeChange('grid');
      setTimeout(() => setShowBatchRegistration(true), 100);
    } else {
      setShowBatchRegistration(true);
    }
  }, [viewMode, handleViewModeChange]);

  const handleRowClick = useCallback((molecule: Molecule, tab?: string) => {
    // Toggle: clicking the same row again closes the panel (unless pinned or switching tabs)
    if (detailPanelMolecule && detailPanelMolecule.id === molecule.id && !tab && !isDetailPinned) {
      setDetailPanelMolecule(null);
      return;
    }
    setDetailPanelMolecule(molecule);
    if (tab) {
      setDetailPanelTab(tab as DetailTab);
    } else if (!detailPanelMolecule || detailPanelMolecule.id !== molecule.id) {
      // Reset to workbench tab when selecting a new molecule via row click
      setDetailPanelTab(null);
    }
  }, [detailPanelMolecule, isDetailPinned]);

  const handleRefLibraryRequest = async () => {
    if (!id || isNaN(Number(id))) return;
    if (refLibraryStatus === 'approved' || refLibraryStatus === 'pending') return;
    try {
      const token = getStoredToken();
      const res = await fetch('/api/v1/reference-library/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ file_id: parseInt(id, 10) || 0 }),
      });
      if (res.ok) {
        const data = await res.json();
        setRefLibraryStatus(data.status);
        if (data.status === 'approved') {
          showNotification('success', 'Added to Reference Library');
        } else {
          showNotification('success', 'Request sent to administrator for approval');
        }
      } else {
        const err = await res.json();
        showNotification('error', err.detail || 'Failed');
      }
    } catch { showNotification('error', 'Failed to submit request'); }
  };

  const handleDownload = async () => {
    if (!id || !currentFile) return;

    // Use display name for user-facing download filename
    const downloadName = currentFile.originalFileName || currentFile.fileName;

    // For large files, use background task with progress
    const LARGE_FILE_THRESHOLD = 10000;
    if (totalMolecules > LARGE_FILE_THRESHOLD) {
      setIsDownloading(true);
      try {
        const result = await chemistryApi.startDownload(id, downloadName);

        // Add task to store
        addTask({
          id: result.taskId,
          type: 'download',
          status: 'running',
          percent: 0,
          fileName: downloadName,
          totalMolecules: result.totalMolecules || totalMolecules,
          fileId: parseInt(id, 10),
        });

        // Start background polling
        startDownloadPolling(result.taskId, downloadName);
      } catch (error) {
        console.error('Download failed:', error);
      } finally {
        setIsDownloading(false);
      }
    } else {
      // For small files, use direct download
      setIsDownloading(true);
      try {
        const blob = await chemistryApi.downloadFile(id);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = downloadName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (error) {
        console.error('Download failed:', error);
      } finally {
        setIsDownloading(false);
      }
    }
  };

  const handleDeduplicate = () => {
    if (!id) return;
    setShowDedupDialog(true);
  };

  // Delete selected molecules
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = useCallback(() => {
    if (selectedMolecules.length === 0) return;
    setShowDeleteConfirm(true);
  }, [selectedMolecules.length]);

  const handleConfirmDelete = useCallback(async () => {
    if (!id || selectedMolecules.length === 0) return;
    setIsDeleting(true);
    setShowDeleteConfirm(false);
    try {
      const moleculeIds = selectedMolecules.map(m => m.id);
      const result = await chemistryApi.deleteMolecules(id, moleculeIds);
      showNotification('success', `Deleted ${result.deletedCount} molecule${result.deletedCount !== 1 ? 's' : ''}`);
      setDeselectAllTrigger(n => n + 1);
      setGridRefreshTrigger(n => n + 1);
    } catch (error) {
      console.error('Failed to delete molecules:', error);
      showNotification('error', 'Failed to delete molecules');
    } finally {
      setIsDeleting(false);
    }
  }, [id, selectedMolecules, showNotification]);

  const handleStandardise = useCallback(async () => {
    if (selectedMolecules.length === 0) return;
    showNotification('success', `Standardising ${selectedMolecules.length} compound${selectedMolecules.length !== 1 ? 's' : ''}...`);
    let successCount = 0;
    for (const mol of selectedMolecules) {
      if (!mol.smiles) continue;
      try {
        await registrationAPI.standardise({ smiles: mol.smiles });
        successCount++;
      } catch {
        // skip failures
      }
    }
    showNotification('success', `Standardised ${successCount} of ${selectedMolecules.length} compounds`);
    if (id) setGridRefreshTrigger(n => n + 1);
  }, [selectedMolecules, id, showNotification]);

  // Record an edit for undo history
  const handleCellEdit = useCallback((moleculeId: number, propertyKey: string, oldValue: any, newValue: any) => {
    setEditHistory(prev => {
      const newHistory = [...prev, { moleculeId, propertyKey, oldValue, newValue }];
      // Limit to 50 entries
      return newHistory.slice(-50);
    });
  }, []);

  // Undo last edit
  const handleUndo = useCallback(async () => {
    if (editHistory.length === 0 || !id) return;

    const lastEdit = editHistory[editHistory.length - 1];
    setEditHistory(prev => prev.slice(0, -1));

    try {
      await chemistryApi.updateMolecule(id, lastEdit.moleculeId, {
        properties: { [lastEdit.propertyKey]: lastEdit.oldValue }
      });
      // Trigger grid refresh
      setGridRefreshTrigger(prev => prev + 1);
      console.log('[EditorPage] Undo successful:', lastEdit);
    } catch (error) {
      console.error('[EditorPage] Undo failed:', error);
      // Re-add to history if undo failed
      setEditHistory(prev => [...prev, lastEdit]);
    }
  }, [editHistory, id]);

  // Keyboard shortcut for undo (Ctrl+Z)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey && !isLocked) {
        e.preventDefault();
        handleUndo();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, isLocked]);

  // Get display filename - prefer originalFileName over fileName
  const displayFileName = currentFile?.originalFileName || currentFile?.fileName || '';

  // Ensure the renamed file keeps its original extension if user omits it
  const ensureExtension = (newName: string): string => {
    const origFileName = currentFile?.fileName || '';
    const origExt = origFileName.split('.').pop()?.toLowerCase() || '';
    if (!origExt) return newName;
    const newExt = newName.split('.').pop()?.toLowerCase() || '';
    if (newExt === origExt) return newName;
    return `${newName}.${origExt}`;
  };

  // Handle file rename (from modal)
  const handleRename = async () => {
    if (!id || !newFileName.trim()) return;
    setIsRenaming(true);
    try {
      const finalName = ensureExtension(newFileName.trim());
      await chemistryApi.renameFile(id, finalName);
      // Refresh file info to update the display
      await fetchFileInfo(id);
      setShowRenameModal(false);
    } catch (error) {
      console.error('Rename failed:', error);
      showNotification('error', 'Failed to rename file');
    } finally {
      setIsRenaming(false);
    }
  };

  // Handle inline title rename (from PageHeader)
  const handleTitleRename = async (newName: string) => {
    if (!id) return;
    setIsRenaming(true);
    try {
      const finalName = ensureExtension(newName);
      await chemistryApi.renameFile(id, finalName);
      await fetchFileInfo(id);
    } catch (error) {
      console.error('[Rename] ERROR:', error);
      throw error; // Re-throw so PageHeader knows it failed
    } finally {
      setIsRenaming(false);
    }
  };

  // Open rename modal with current filename
  const openRenameModal = () => {
    setNewFileName(displayFileName);
    setShowRenameModal(true);
  };

  // Determine which molecules to display
  // Priority: attributeFilteredMolecules > searchResults > molecules
  // attributeFilteredMolecules is already a filtered subset of searchResults or molecules
  const displayMolecules = useMemo(() => {
    // Attribute filter results take priority (they're already filtered from structure search or all molecules)
    if (attributeFilteredMolecules) {
      if (showMatchesOnly) {
        return attributeFilteredMolecules;
      } else {
        // Show all with highlights - for large files, stay in SSRM mode
        return molecules.length > 0 ? molecules : (searchResults?.molecules || []);
      }
    }
    // Structure search results (no attribute filter applied)
    if (searchResults) {
      if (showMatchesOnly) {
        return searchResults.molecules;
      } else {
        // If molecules are loaded (small file), show all with highlights
        // For large files (SSRM), return empty array to stay in SSRM mode
        return molecules.length > 0 ? molecules : [];
      }
    }
    // Default to all molecules
    return molecules;
  }, [searchResults, attributeFilteredMolecules, molecules, showMatchesOnly]);

  // Compute highlighted IDs for structure search when showing all
  const effectiveHighlightedIds = useMemo(() => {
    if (searchResults && !showMatchesOnly) {
      // Create set of matching molecule IDs from search results
      return new Set(searchResults.molecules.map(m => m.id));
    }
    return highlightedIds;
  }, [searchResults, showMatchesOnly, highlightedIds]);

  // Compute transposed grid data when transpose view is active
  const transposedGridData = useMemo(() => {
    if (!isTransposed) return null;
    return transposeMolecules(displayMolecules, columns);
  }, [isTransposed, displayMolecules, columns, transposeMolecules]);

  // Determine if we should use client-side mode for the grid
  // Client-side mode is used for: transposed view, small files, or when displaying filtered results
  // For large files with search results and showMatchesOnly=false, stay in SSRM mode to show all molecules
  const shouldUseClientSide = isTransposed ||
    !useSSRM ||
    (!!searchResults && showMatchesOnly) ||
    (!!attributeFilteredMolecules && showMatchesOnly);

  console.log('[DEBUG EditorPage] shouldUseClientSide:', shouldUseClientSide,
    '{ isTransposed:', isTransposed, ', !useSSRM:', !useSSRM,
    ', searchResults:', !!searchResults, ', attributeFilter:', !!attributeFilteredMolecules, '}');

  // Compute matchIds for SSRM filtering when search is active
  // This preserves search context when sorting/paginating in SSRM mode
  const ssrmMatchIds = useMemo(() => {
    // Not needed when in client-side mode (data already filtered)
    if (shouldUseClientSide) return null;

    // Structure search results - filter SSRM to these IDs
    if (searchResults) {
      return new Set(searchResults.molecules.map(m => m.id));
    }

    // Attribute filter results - filter SSRM to these IDs
    if (attributeFilteredMolecules) {
      return new Set(attributeFilteredMolecules.map(m => m.id));
    }

    return null;
  }, [shouldUseClientSide, searchResults, attributeFilteredMolecules]);

  return (
    <div className="flex flex-col h-full">
      {/* Layer 4: Dynamic watermark for guest/trial users */}
      <DynamicWatermark />
      {/* Secure share banner for shared file recipients */}
      {shareState?.shareToken && (
        <SecureShareBanner
          shareToken={shareState.shareToken}
          sharedByName={shareState.sharedByName}
          permission={shareState.permission}
        />
      )}
      {/* Page Header — slim bar in IP Intelligence mode, full header otherwise */}
      {viewMode === 'patentMap' ? (
        <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-teal-600 to-emerald-600 text-white" style={{ minHeight: 44 }}>
          <button
            onClick={() => handleViewModeChange('grid')}
            className="text-sm text-white/90 hover:text-white flex items-center gap-1 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Grid
          </button>
          <span className="text-sm font-medium truncate mx-4">{displayFileName}</span>
          <div className="flex items-center gap-2">
            <GuestGate tooltip="Enrich compounds with patent, target, and pathway data. Available to Members." enabled={isMember} onUpgrade={() => setShowUpgradePrompt(true)}>
              {id && !isAllCompoundsMode && (
                <RunIntelligenceButton
                  fileId={id}
                  onComplete={() => setGridRefreshTrigger(n => n + 1)}
                />
              )}
            </GuestGate>
            <GuestGate tooltip="Submit files for the shared reference library. Available to Members." enabled={isMember} onUpgrade={() => setShowUpgradePrompt(true)}>
              <button
                onClick={handleRefLibraryRequest}
                disabled={refLibraryStatus === 'approved' || refLibraryStatus === 'pending'}
                className={`px-2 py-1 text-xs border rounded-lg transition-colors flex items-center gap-1 ${
                  refLibraryStatus === 'approved'
                    ? 'text-green-300 bg-green-900/30 border-green-700 cursor-default'
                    : refLibraryStatus === 'pending'
                    ? 'text-amber-300 bg-amber-900/30 border-amber-700 cursor-default'
                    : 'text-white bg-white/20 border-white/30 hover:bg-white/30'
                }`}
                title={isAdmin
                  ? 'Reference Library — Add this file to the shared reference library'
                  : 'Reference Library — Submit this file for inclusion in the shared reference library. Administrator approval required.'}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                {refLibraryStatus === 'approved' ? 'In Ref Library' : refLibraryStatus === 'pending' ? 'Pending Approval' : 'Ref Library'}
              </button>
            </GuestGate>
          </div>
        </div>
      ) : (
      <PageHeader
        title={isAllCompoundsMode ? `All Compounds${allCompoundsProject ? ` — ${allCompoundsProject.name}` : ''}` : (displayFileName || 'Loading...')}
        subtitle={isAllCompoundsMode
          ? `${(allCompoundsTotalCount || totalMolecules).toLocaleString()} molecules across all files`
          : `${(isReactionsFile ? (reactionCount ?? 0) : totalMolecules).toLocaleString()} ${isBiologicsFile ? 'sequences' : isReactionsFile ? 'reactions' : 'molecules'}${currentFile?.propertiesCount ? ` · ${currentFile.propertiesCount} properties` : ''}`}
        backTo={currentProject ? `/chemistry/files?project=${fromProjectId || currentProject.id}&view=files` : '/chemistry/projects'}
        backLabel={currentProject?.name || 'Projects'}
        breadcrumbs={[
          { label: 'Projects', to: '/chemistry/projects' },
          ...(currentProject ? [{ label: currentProject.name, to: `/chemistry/files?project=${fromProjectId || currentProject.id}&view=files` }] : []),
        ]}
        onTitleChange={isAllCompoundsMode ? undefined : handleTitleRename}
        isTitleSaving={isRenaming}
        actions={
          <div className="flex items-center gap-1.5">
            {/* Lock/Unlock Toggle */}
            <button
              onClick={() => setIsLocked(!isLocked)}
              className={`px-2 py-1.5 text-xs flex items-center gap-1.5 rounded-md border transition-colors ${
                isLocked
                  ? 'text-white bg-white/20 border-white/30 hover:bg-white/30'
                  : 'text-emerald-900 bg-emerald-400 border-emerald-500 hover:bg-emerald-300'
              }`}
              title={isLocked ? 'File is locked — click to enable editing' : 'File is unlocked — click to lock and prevent edits'}
            >
              {isLocked ? (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
              )}
              {isLocked ? 'Locked' : 'Editing'}
            </button>
            {/* Undo Button — disabled until properly wired */}
            {!isLocked && (
              <button
                disabled
                className="px-2 py-1.5 text-xs text-white/40 bg-white/10 border border-white/15 rounded-md cursor-not-allowed transition-colors flex items-center gap-1 opacity-40"
                title="Undo — coming soon"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
                {bp === 'wide' && <>Undo</>}
              </button>
            )}
            {/* Molecule/protein export is not valid for reaction files. */}
            {!isReactionsFile && <GuestGate tooltip="Export molecular data in SDF, CSV, or Excel format. Available to Members." enabled={isMember} onUpgrade={() => setShowUpgradePrompt(true)}>
              <ExportButton
                dataType={isBiologicsFile ? 'proteins' : 'molecules'}
                totalCount={totalMolecules}
                selectedCount={selectedMoleculeIds.size}
                fileId={id ? Number(id) : undefined}
                moleculeIds={selectedMoleculeIds.size > 0 ? Array.from(selectedMoleculeIds) : undefined}
                proteinIds={selectedMoleculeIds.size > 0 ? Array.from(selectedMoleculeIds) : undefined}
                className="px-2 py-1.5 text-xs text-white bg-white/20 border border-white/30 rounded-md hover:bg-white/30 disabled:opacity-50 transition-colors flex items-center gap-1"
                compact
                openTrigger={exportTrigger}
              />
            </GuestGate>}
            {/* Register button */}
            {!isReactionsFile && !isBiologicsFile && (
              <GuestGate tooltip="Register compounds to the registry. Available to Members." enabled={isMember} onUpgrade={() => setShowUpgradePrompt(true)}>
                <button
                  onClick={handleRegister}
                  disabled={selectedMolecules.length === 0}
                  className="px-2 py-1.5 text-xs text-white bg-white/20 border border-white/30 rounded-md hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                  title={selectedMolecules.length > 0 ? `Register ${selectedMolecules.length} selected compound${selectedMolecules.length !== 1 ? 's' : ''} to the corporate registry` : 'Select compounds first, then register them to the corporate registry'}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  {bp !== 'narrow' && <>Register{selectedMolecules.length > 0 ? ` (${selectedMolecules.length})` : ''}</>}
                </button>
              </GuestGate>
            )}
            {/* Compare button — shows when 2-4 compounds selected */}
            {selectedMolecules.length >= 2 && selectedMolecules.length <= 4 && (
              <button
                onClick={() => setShowComparison(true)}
                className="px-2 py-1.5 text-xs text-white bg-white/20 border border-white/30 rounded-md hover:bg-white/30 transition-colors flex items-center gap-1"
                title={`Compare ${selectedMolecules.length} selected compounds side-by-side with property deltas`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Compare ({selectedMolecules.length})
              </button>
            )}
            {/* Compare All button */}
            {bp !== 'narrow' && totalMolecules > 0 && !isReactionsFile && !isBiologicsFile && (
              <button
                onClick={() => setShowCompareAll(true)}
                className="px-2 py-1.5 text-xs text-white bg-white/20 border border-white/30 rounded-md hover:bg-white/30 transition-colors flex items-center gap-1"
                title="Compare All — Open a sortable property table with all compounds side-by-side"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18M3 6h18M3 18h18" />
                </svg>
                {bp === 'wide' && <span>Compare All</span>}
              </button>
            )}
            {/* Share button */}
            <GuestGate tooltip="Share files securely with colleagues. Available to Members." enabled={isMember} onUpgrade={() => setShowUpgradePrompt(true)}>
              <button
                onClick={() => {
                  const briefingSeen = sessionStorage.getItem('share_briefing_seen');
                  if (briefingSeen) {
                    setShowShareDialog(true);
                  } else {
                    setShowSecurityBriefing(true);
                  }
                }}
                className="px-2 py-1.5 text-xs text-white bg-white/20 border border-white/30 rounded-md hover:bg-white/30 transition-colors flex items-center gap-1"
                title="Share — Send a secure, time-limited link to colleagues via email"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                {bp === 'wide' && <span>Share</span>}
              </button>
            </GuestGate>
          </div>
        }
      >
        {/* ── Row 2: Data Operations ── */}
        <div className="page-header__row2">
          {/* Left: View mode segmented control */}
          <div className="flex items-center gap-2">
            {isReactionsFile ? (
              <span className="text-xs text-white/80">
                Reaction table · click a Reaction SMILES or double-click a row to edit
              </span>
            ) : (
              <ViewModeSwitcher activeMode={viewMode} onModeChange={handleViewModeChange} />
            )}
          </div>

          {/* Centre: IP Intelligence + Enrich */}
          {!isReactionsFile && <div className="flex items-center gap-1.5">
            {/* IP Intelligence — standalone button for patent map (navigates to slim bar mode) */}
            {!isReactionsFile && !isBiologicsFile && !isAllCompoundsMode && (
              <button
                onClick={() => handleViewModeChange('patentMap')}
                className="px-2.5 py-1 text-xs rounded-md border transition-colors flex items-center gap-1.5 text-white/80 bg-white/10 border-white/20 hover:bg-white/20 hover:text-white"
                title="IP Intelligence — Search your compounds against 28M+ patent structures to map IP risk zones"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                {bp !== 'narrow' && <span>IP Intelligence</span>}
              </button>
            )}
            {/* Target Universe — file-level target landscape */}
            {!isReactionsFile && !isBiologicsFile && (
              <button
                onClick={() => handleViewModeChange('targetUniverse')}
                className="px-2.5 py-1 text-xs rounded-md border transition-colors flex items-center gap-1.5 text-white/80 bg-white/10 border-white/20 hover:bg-white/20 hover:text-white"
                title="Target Universe — Visualise the target landscape for compounds in this file"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" strokeWidth={2} />
                  <circle cx="12" cy="12" r="4" strokeWidth={2} />
                  <line x1="12" y1="2" x2="12" y2="8" strokeWidth={2} />
                  <line x1="12" y1="16" x2="12" y2="22" strokeWidth={2} />
                  <line x1="2" y1="12" x2="8" y2="12" strokeWidth={2} />
                  <line x1="16" y1="12" x2="22" y2="12" strokeWidth={2} />
                </svg>
                {bp !== 'narrow' && <span>Target Universe</span>}
              </button>
            )}
            {/* SAR Analysis — standalone button */}
            {!isReactionsFile && !isBiologicsFile && (
              <button
                onClick={() => handleViewModeChange('sar')}
                className="px-2.5 py-1 text-xs rounded-md border transition-colors flex items-center gap-1.5 text-white/80 bg-white/10 border-white/20 hover:bg-white/20 hover:text-white"
                title="SAR Analysis — Structure-activity scatter plot with Lipinski drug-likeness overlay"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="6" cy="18" r="1.5" fill="currentColor" stroke="none" />
                  <circle cx="10" cy="12" r="1.5" fill="currentColor" stroke="none" />
                  <circle cx="14" cy="8" r="1.5" fill="currentColor" stroke="none" />
                  <circle cx="18" cy="14" r="1.5" fill="currentColor" stroke="none" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 21V3m0 18h18" />
                </svg>
                {bp !== 'narrow' && <span>SAR</span>}
              </button>
            )}
            {/* Statistics / Analyse — standalone button */}
            <button
              onClick={() => handleViewModeChange('analyse')}
              className="px-2.5 py-1 text-xs rounded-md border transition-colors flex items-center gap-1.5 text-white/80 bg-white/10 border-white/20 hover:bg-white/20 hover:text-white"
              title="Statistics / Analyse — Scatter plots, histograms, box plots, and property correlations"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              {bp !== 'narrow' && <span>Statistics</span>}
            </button>
            {/* Add to Ref Library — standalone button */}
            <button
              onClick={handleRefLibraryRequest}
              disabled={refLibraryStatus === 'approved' || refLibraryStatus === 'pending'}
              className="px-2.5 py-1 text-xs rounded-md border transition-colors flex items-center gap-1.5 text-white/80 bg-white/10 border-white/20 hover:bg-white/20 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              title={isAdmin ? 'Add this file to the shared reference library' : 'Submit this file for inclusion in the shared reference library'}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              {bp !== 'narrow' && <span>{refLibraryStatus === 'approved' ? 'In Library' : refLibraryStatus === 'pending' ? 'Pending' : 'Ref Library'}</span>}
              {refLibraryStatus === 'approved' && (
                <span className="text-[9px] px-1 py-0.5 rounded-full bg-green-400/30 text-green-200 font-medium">Active</span>
              )}
              {refLibraryStatus === 'pending' && (
                <span className="text-[9px] px-1 py-0.5 rounded-full bg-amber-400/30 text-amber-200 font-medium">Pending</span>
              )}
            </button>
            {/* Enrich — primary accent button */}
            <GuestGate tooltip="Enrich compounds with patent, target, and pathway data. Available to Members." enabled={isMember} onUpgrade={() => setShowUpgradePrompt(true)}>
              {id && !isAllCompoundsMode && (
                <RunIntelligenceButton
                  fileId={id}
                  onComplete={() => setGridRefreshTrigger(n => n + 1)}
                  startTrigger={enrichTrigger}
                />
              )}
            </GuestGate>
          </div>}

          {/* Right: Table Management + Overflow */}
          <div className="flex items-center gap-1.5">
            {!isReactionsFile && <TableManagementDropdown
              columns={columns}
              getGridColumns={getGridColumns}
              selectedCount={selectedMolecules.length}
              selectedRows={selectedMolecules.map(m => ({
                id: m.id,
                smiles: m.smiles,
                properties: m.properties
              }))}
              fileId={id}
              onDeduplicate={handleDeduplicate}
              onRefresh={() => {
                if (id) {
                  fetchMolecules(id);
                  setGridRefreshTrigger(n => n + 1);
                }
              }}
              isTransposed={isTransposed}
              onToggleTranspose={handleToggleTranspose}
              showHeatmap={showHeatmap}
              onToggleHeatmap={() => setShowHeatmap(!showHeatmap)}
              heatmapSettings={heatmapSettings}
              onHeatmapSettingsChange={setHeatmapSettings}
              columnStats={columnStats}
              onOpenHeatmapPanel={() => setShowHeatmapPanel(true)}
              onResetColumnWidths={() => setResetColumnsTrigger(n => n + 1)}
              onColumnVisibilityChange={(columnId, visible) => {
                setColumnVisibilityAction({ columnId, visible });
                setColumnVisibilityTrigger(n => n + 1);
              }}
            />}
            <OverflowMenu
              items={[
                ...(bp === 'narrow' && totalMolecules > 0 && !isReactionsFile && !isBiologicsFile ? [{
                  id: 'compareAll',
                  label: 'Compare All',
                  tooltip: 'Open a sortable property table with all compounds side-by-side',
                  icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18M3 6h18M3 18h18" />
                    </svg>
                  ),
                  onClick: () => setShowCompareAll(true),
                }] : []),
                {
                  id: 'shareActivity',
                  label: 'Sharing Activity',
                  tooltip: 'See who has access to this file and audit the full sharing history',
                  icon: (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  ),
                  onClick: () => setShowShareActivity(true),
                },
              ] as OverflowItem[]}
            />
          </div>
        </div>
      </PageHeader>
      )}

      {/* Import progress banner */}
      {importProgress && (
        <div style={{
          padding: '8px 16px',
          background: importProgress.status === 'completed' || importProgress.status === 'completed_with_errors'
            ? 'rgba(16, 185, 129, 0.12)'
            : importProgress.status === 'error' || importProgress.status === 'failed' || importProgress.status === 'cancelled'
              ? 'rgba(239, 68, 68, 0.12)'
              : 'rgba(59, 130, 246, 0.12)',
          borderBottom: '1px solid var(--card-border)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '13px',
          flexShrink: 0,
        }}>
          {/* Spinner for active processing */}
          {(importProgress.status === 'processing' || importProgress.status === 'pending') && (
            <div style={{
              width: 14, height: 14, borderRadius: '50%',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              borderTopColor: '#3b82f6',
              animation: 'spin 0.8s linear infinite',
            }} />
          )}
          {(importProgress.status === 'completed' || importProgress.status === 'completed_with_errors') && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5">
              <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
          {(importProgress.status === 'error' || importProgress.status === 'failed' || importProgress.status === 'cancelled') && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}

          {/* Text */}
          <span style={{ color: 'var(--text-primary)', fontWeight: 500, fontFamily: 'var(--font-mono, monospace)' }}>
            {importProgress.status === 'pending' && 'Queued for processing...'}
            {importProgress.status === 'processing' && (
              importProgress.total > 0
                ? `Importing: ${importProgress.processed.toLocaleString()} / ${importProgress.total.toLocaleString()} molecules (${Math.round((importProgress.processed / importProgress.total) * 100)}%)`
                : `Importing: ${importProgress.processed.toLocaleString()} molecules...`
            )}
            {(importProgress.status === 'completed' || importProgress.status === 'completed_with_errors') &&
              `Import complete: ${importProgress.processed.toLocaleString()} molecules`}
            {(importProgress.status === 'error' || importProgress.status === 'failed') && 'Import failed'}
            {importProgress.status === 'cancelled' && 'Import cancelled'}
          </span>

          {/* Progress bar */}
          {importProgress.status === 'processing' && importProgress.total > 0 && (
            <div style={{
              flex: 1, maxWidth: 200, height: 4, borderRadius: 2,
              background: 'rgba(59, 130, 246, 0.15)',
              overflow: 'hidden',
            }}>
              <div style={{
                width: `${Math.min(100, Math.round((importProgress.processed / importProgress.total) * 100))}%`,
                height: '100%',
                borderRadius: 2,
                background: '#3b82f6',
                transition: 'width 0.3s ease',
              }} />
            </div>
          )}
        </div>
      )}

      {/* Main Content - z-10 ensures it's below the header's z-20 for dropdown visibility */}
      <div className="flex flex-1 overflow-hidden relative z-10">
        {/* File Panel — shows project files for quick switching */}
        {currentFile?.projectId && (
          <FilePanel
            projectId={currentFile.projectId}
            activeFileId={isAllCompoundsMode ? undefined : id}
            isAllCompoundsActive={isAllCompoundsMode}
          />
        )}
        {/* Left Panel - Search (collapsible) */}
        <div
          className={`bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-200 ${
            sidebarCollapsed ? 'w-12' : 'w-80'
          }`}
        >
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-3 hover:bg-gray-100 dark:hover:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-center"
            title={sidebarCollapsed ? 'Expand search panel' : 'Collapse search panel'}
          >
            <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
          {!sidebarCollapsed && (
            <SearchPanel
              fileId={id || ''}
              columns={columns}
              molecules={molecules}
              onFilteredResults={handleFilteredResults}
              showMatchesOnly={showMatchesOnly}
              onShowMatchesOnlyChange={handleShowMatchesOnlyChange}
            />
          )}
        </div>

        {/* Molecule Grid */}
        <div className="flex-1 flex flex-col overflow-hidden relative">
          {/* Search Results Bar - Structure Search */}
          {searchResults && (
            <div className="px-4 py-2 bg-emerald-50 dark:bg-emerald-900/30 border-b border-emerald-200 dark:border-emerald-800 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm text-emerald-700">
                  Found {searchResults.total} matching {isReactionsFile ? 'reactions' : 'molecules'} (structure search)
                  {searchResults.searchContext && ` - ${searchResults.searchContext}`}
                </span>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showMatchesOnly}
                    onChange={(e) => setShowMatchesOnly(e.target.checked)}
                    className="rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
                  />
                  <span className="ml-2 text-xs text-emerald-600">Show matches only</span>
                </label>
              </div>
              <button
                onClick={() => {
                  clearSearch();
                  handleFilteredResults(null, null);
                  setShowMatchesOnly(true);
                }}
                className="text-sm text-emerald-600 hover:text-emerald-800"
              >
                Clear search
              </button>
            </div>
          )}

          {/* Search Results Bar - Attribute Search */}
          {!searchResults && attributeFilteredMolecules && (
            <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 flex items-center justify-between">
              <span className="text-sm text-amber-700 dark:text-amber-300">
                Found {attributeFilteredMolecules.length} matching {isReactionsFile ? 'reactions' : 'molecules'} (attribute filter)
              </span>
              <button
                onClick={() => {
                  handleFilteredResults(null, null);
                  setShowMatchesOnly(true);
                }}
                className="text-sm text-amber-600 hover:text-amber-800"
              >
                Clear filter
              </button>
            </div>
          )}

          {/* Highlight indicator when showing all with highlights */}
          {!searchResults && !attributeFilteredMolecules && highlightedIds && highlightedIds.size > 0 && (
            <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 flex items-center justify-between">
              <span className="text-sm text-amber-700 dark:text-amber-300">
                {highlightedIds.size} {isReactionsFile ? 'reactions' : 'molecules'} highlighted
              </span>
              <button
                onClick={() => {
                  handleFilteredResults(null, null);
                  setShowMatchesOnly(true);
                }}
                className="text-sm text-amber-600 hover:text-amber-800"
              >
                Clear highlights
              </button>
            </div>
          )}

          {isSearching && (
            <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/30 border-b border-blue-200 dark:border-blue-800">
              <span className="text-sm text-blue-700 dark:text-blue-300">Searching...</span>
            </div>
          )}

          {moleculesError ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-red-500">{moleculesError}</div>
            </div>
          ) : isBiologicsFile ? (
            <div className="flex-1 overflow-hidden p-4">
              <BiologicsGrid
                fileId={parseInt(id || '0', 10)}
              />
            </div>
          ) : isReactionsFile ? (
            <div className="flex-1 overflow-hidden p-4">
              <ReactionGrid
                fileId={parseInt(id || '0', 10)}
                onSearchStructure={handleSearchParticipant}
              />
            </div>
          ) : viewMode === 'patentMap' && id ? (
            <div className="flex-1 overflow-hidden transition-colors duration-200" style={{ background: 'var(--content-bg)', borderTop: '2px solid var(--brand-primary)' }}>
              <PatentCollisionMap
                fileId={isAllCompoundsMode ? '0' : id}
                projectId={isAllCompoundsMode ? allCompoundsProjectId ?? undefined : undefined}
                onMoleculeSelect={(mol) => {
                  if (mol?.smiles) {
                    handleRowClick({
                      id: mol.id || 0,
                      mol_index: 0,
                      molfile: '',
                      smiles: mol.smiles,
                      properties: mol.properties || {},
                    }, 'patents');
                  }
                }}
              />
            </div>
          ) : viewMode === 'analyse' ? (
            <div className="flex-1 overflow-hidden">
              <AnalyseView
                fileId={parseInt(id || '0', 10)}
                molecules={displayMolecules}
                propertyColumns={numericPropertyColumns}
                selectedMoleculeIds={selectedMoleculeIds}
                onSelectionChange={setSelectedMoleculeIds}
                onMoleculeClick={(molId) => {
                  const mol = displayMolecules.find(m => m.id === molId);
                  if (mol) handleRowClick(mol);
                }}
                patentZoneColumn="patent_max_zone"
                columnUnitsMap={columnUnitsMap}
                unitOverrides={unitOverrides}
              />
            </div>
          ) : viewMode === 'sar' ? (
            <div className="flex-1 overflow-hidden">
              <SARScatterChart
                molecules={displayMolecules}
                onMoleculeClick={(molId) => {
                  const mol = displayMolecules.find(m => m.id === molId);
                  if (mol) handleRowClick(mol);
                }}
                columnUnitsMap={columnUnitsMap}
              />
            </div>
          ) : viewMode === 'targetUniverse' ? (
            <div className="flex-1 overflow-auto p-4" style={{ background: 'var(--content-bg)' }}>
              {currentFile?.projectId ? (
                <ProjectTargetUniverseSection projectId={currentFile.projectId} />
              ) : (
                <div className="flex flex-col items-center justify-center py-20 gap-3">
                  <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" strokeWidth={1.5} />
                    <circle cx="12" cy="12" r="4" strokeWidth={1.5} />
                  </svg>
                  <p className="text-sm text-gray-500">Target Universe requires a project context.</p>
                  <p className="text-xs text-gray-400">Add this file to a project to view the target landscape.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Transpose truncation banner */}
              {isTransposed && transposedGridData?.truncated && (
                <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/30 border-b border-blue-200 dark:border-blue-800 flex items-center gap-2 flex-shrink-0">
                  <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm text-blue-700">
                    Showing first {TRANSPOSE_MOL_LIMIT} of {displayMolecules.length.toLocaleString()} molecules in transpose view. Use filters to narrow your selection.
                  </span>
                </div>
              )}
              <div className="flex-1 overflow-hidden">
                <MoleculeGrid
                  key={isAllCompoundsMode ? 'all-compounds' : (shouldUseClientSide ? 'client' : 'server')}
                  fileId={id || ''}
                  fileName={isAllCompoundsMode ? 'All Compounds' : displayFileName}
                  isLoading={isLoadingMolecules}
                  highlightedIds={effectiveHighlightedIds || undefined}
                  onMoleculeUpdate={() => id && !isAllCompoundsMode && fetchMolecules(id)}
                  onSelectionChange={setSelectedMolecules}
                  clientData={(!isAllCompoundsMode && shouldUseClientSide) ? (isTransposed ? (transposedGridData?.data ?? []) : displayMolecules) : undefined}
                  clientColumns={isTransposed ? transposedGridData?.columns : undefined}
                  isTransposed={isTransposed}
                  showHeatmap={showHeatmap}
                  heatmapSettings={heatmapSettings}
                  onColumnStatsChange={setColumnStats}
                  matchIds={ssrmMatchIds}
                  refreshTrigger={gridRefreshTrigger}
                  resetColumnsTrigger={resetColumnsTrigger}
                  selectAllTrigger={selectAllTrigger}
                  deselectAllTrigger={deselectAllTrigger}
                  isLocked={isAllCompoundsMode ? true : isLocked}
                  onCellEdit={handleCellEdit}
                  onRowClick={handleRowClick}
                  selectedCompoundId={detailPanelMolecule?.id ?? null}
                  navigationFocus={workbenchFocus}
                  columnUnitsMap={columnUnitsMap}
                  onUnitOverridesChange={setUnitOverrides}
                  ssrmQueryFn={isAllCompoundsMode ? allCompoundsQueryFn : undefined}
                  columnVisibilityTrigger={columnVisibilityTrigger}
                  columnVisibilityAction={columnVisibilityAction}
                  onGridApiReady={(api) => { gridApiRef.current = api; }}
                />
              </div>
            </div>
          )}

          {/* Selection Action Bar — slides up when compounds are selected */}
          {!isReactionsFile && <SelectionActionBar
            selectedMolecules={selectedMolecules}
            totalMolecules={totalMolecules}
            onSelectAll={() => setSelectAllTrigger(n => n + 1)}
            onClearSelection={() => setDeselectAllTrigger(n => n + 1)}
            onRegister={handleRegister}
            onCompare={() => setShowCompareAll(true)}
            onStandardise={handleStandardise}
            onEnrich={() => setEnrichTrigger(n => n + 1)}
            onExport={() => setExportTrigger(n => n + 1)}
            onDeduplicate={handleDeduplicate}
            onDelete={handleDelete}
            onSubmitForAssay={() => setShowAssayModal(true)}
            onQuickSearch={() => {
              const molecule = selectedMolecules[0];
              if (molecule) navigate(`/chemistry/quick-search?q=${encodeURIComponent(molecule.smiles || String(molecule.id))}`);
            }}
            hideChemistryActions={isReactionsFile || isBiologicsFile}
          />}
        </div>

        {/* Compound Detail Panel */}
        <CompoundDetailPanel
          molecule={detailPanelMolecule}
          activeTab={detailPanelTab}
          onClose={() => setDetailPanelMolecule(null)}
          onTabChange={setDetailPanelTab}
          isPinned={isDetailPinned}
          onPinChange={setIsDetailPinned}
          onPropertiesSaved={() => setGridRefreshTrigger(n => n + 1)}
          projectId={currentFile?.projectId}
        />
      </div>

      {/* IP Intelligence is now inline via viewMode === 'patentMap' */}

      {/* Floating Heatmap Settings Panel — only rendered in heatmap view */}
      {viewMode === 'heatmap' && (
        <HeatmapPanel
          isOpen={showHeatmapPanel}
          onClose={() => setShowHeatmapPanel(false)}
          heatmapSettings={heatmapSettings}
          onHeatmapSettingsChange={setHeatmapSettings}
          columnStats={columnStats}
        />
      )}

      {/* Rename Modal */}
      {showRenameModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: 1100 }}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Rename File</h3>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                File Name
              </label>
              <input
                type="text"
                value={newFileName}
                onChange={(e) => setNewFileName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isRenaming) handleRename();
                  if (e.key === 'Escape') setShowRenameModal(false);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Enter new file name"
                autoFocus
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setShowRenameModal(false)}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRename}
                disabled={isRenaming || !newFileName.trim()}
                className="px-4 py-2 text-sm text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-50 transition-colors"
              >
                {isRenaming ? 'Renaming...' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Security Briefing → Share Dialog */}
      <ShareSecurityBriefing
        isOpen={showSecurityBriefing}
        onContinue={() => {
          sessionStorage.setItem('share_briefing_seen', '1');
          setShowSecurityBriefing(false);
          setShowShareDialog(true);
        }}
        onCancel={() => setShowSecurityBriefing(false)}
      />
      {currentFile && (
        <ShareDialog
          isOpen={showShareDialog}
          onClose={() => setShowShareDialog(false)}
          fileId={parseInt(id || '0')}
          fileName={currentFile.originalFileName || currentFile.fileName}
        />
      )}

      {/* Share Activity Panel */}
      {currentFile && (
        <ShareActivityPanel
          isOpen={showShareActivity}
          onClose={() => setShowShareActivity(false)}
          fileId={parseInt(id || '0')}
          fileName={currentFile.originalFileName || currentFile.fileName}
        />
      )}

      {/* Duplicate Dialog */}
      {currentFile && (
        <DuplicateDialog
          isOpen={showDuplicateDialog}
          onClose={() => setShowDuplicateDialog(false)}
          fileId={parseInt(id || '0')}
          fileName={currentFile.originalFileName || currentFile.fileName}
        />
      )}

      {/* Dedup Dialog */}
      {currentFile && (
        <DedupDialog
          isOpen={showDedupDialog}
          onClose={() => setShowDedupDialog(false)}
          fileId={id || '0'}
          fileName={currentFile.originalFileName || currentFile.fileName}
          columns={columns}
          projectId={isAllCompoundsMode ? allCompoundsProjectId ?? undefined : undefined}
          onComplete={() => {
            if (isAllCompoundsMode) {
              setGridRefreshTrigger(n => n + 1);
            } else if (id) {
              fetchFileInfo(id);
              fetchMolecules(id);
              setGridRefreshTrigger(n => n + 1);
            }
          }}
        />
      )}

      {/* Upgrade Prompt */}
      <UpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
      />

      {/* Compound Comparison Modal */}
      {showComparison && selectedMolecules.length >= 2 && (
        <CompoundComparison
          molecules={selectedMolecules.slice(0, 4)}
          onClose={() => setShowComparison(false)}
        />
      )}

      {/* Compare All Table Modal */}
      {showCompareAll && displayMolecules.length > 0 && (
        <CompareAllTable
          molecules={displayMolecules}
          fileId={id}
          onClose={() => setShowCompareAll(false)}
          onMoleculeClick={(mol) => {
            setShowCompareAll(false);
            handleRowClick(mol);
          }}
        />
      )}

      {/* Batch Registration Modal */}
      <BatchRegistrationModal
        isOpen={showBatchRegistration}
        onClose={() => setShowBatchRegistration(false)}
        molecules={selectedMolecules}
      />

      {/* Submit for Assay Modal (batch) */}
      <SubmitForAssayModal
        isOpen={showAssayModal}
        onClose={() => setShowAssayModal(false)}
        moleculeId={selectedMolecules[0]?.id || 0}
        moleculeIds={selectedMolecules.map(m => m.id)}
        projectId={currentProject?.id || 0}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: 10000 }}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-96 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Delete Molecules</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Are you sure you want to delete <span className="font-semibold">{selectedMolecules.length}</span> molecule{selectedMolecules.length !== 1 ? 's' : ''}?
                  </p>
                  <p className="text-xs text-gray-500 mt-1">This action cannot be undone.</p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditorPage;
